import strings from "../core/translate";

/**
 * The work order run, as the garage app knows it. Kept here so the list, the
 * manage screen and its dialogs all read the same stages, the same item types
 * and the same rules about what may still be edited.
 *
 * Statuses and item types mirror the backend enums exactly (see
 * work-order-status.enum.ts / work-order-item-type.enum.ts) — the string is
 * what travels over the wire, so nothing is renamed on the way in.
 */
export const WorkOrderStatus = {
  DRAFT: "draft",
  SENT: "sent",
  PARTIALLY_APPROVED: "partially_approved",
  FULLY_APPROVED: "fully_approved",
  DECLINED: "declined",
  QUOTED: "quoted",
  ACCEPTED: "accepted",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  READY_TO_DELIVER: "ready_to_deliver",
  DELIVERED: "delivered",
};

export const ItemType = {
  PRIORITY: "priority",
  OPTIONAL: "optional",
  SERVICE: "service",
};

/**
 * What a finding can be filed under. "Service" is not on the list: the labour
 * the garage charges for is no longer built up item by item, it is the single
 * services cost the worker names as the final quote goes out. The type itself
 * stays — orders quoted before that still carry their service items, and the
 * report goes on grouping and editing them.
 */
export const ADDABLE_ITEM_TYPES = [ItemType.PRIORITY, ItemType.OPTIONAL];

export const ItemStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export const PaymentStatus = {
  AWAITING_CASH: "awaiting_cash",
  PAID: "paid",
};

// The order the run moves through, used for the stage rail. Declined is not a
// step on it: it ends the run from wherever it was.
export const STAGES = [
  WorkOrderStatus.DRAFT,
  WorkOrderStatus.SENT,
  WorkOrderStatus.FULLY_APPROVED,
  WorkOrderStatus.QUOTED,
  WorkOrderStatus.ACCEPTED,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.DONE,
  WorkOrderStatus.READY_TO_DELIVER,
  WorkOrderStatus.DELIVERED,
];

// Partial approval sits at the same point of the run as full approval — the
// client has answered either way, and the quote is what comes next.
const STAGE_ALIASES = {
  [WorkOrderStatus.PARTIALLY_APPROVED]: WorkOrderStatus.FULLY_APPROVED,
};

export const stageIndexOf = (status) =>
  STAGES.indexOf(STAGE_ALIASES[status] ?? status);

/** `tone` names a colour in work-orders.css, not a colour itself. */
const STATUS_TONES = {
  [WorkOrderStatus.DRAFT]: "muted",
  [WorkOrderStatus.SENT]: "warning",
  [WorkOrderStatus.PARTIALLY_APPROVED]: "info",
  [WorkOrderStatus.FULLY_APPROVED]: "success",
  [WorkOrderStatus.DECLINED]: "danger",
  [WorkOrderStatus.QUOTED]: "warning",
  [WorkOrderStatus.ACCEPTED]: "success",
  [WorkOrderStatus.IN_PROGRESS]: "primary",
  [WorkOrderStatus.DONE]: "success",
  [WorkOrderStatus.READY_TO_DELIVER]: "sky",
  [WorkOrderStatus.DELIVERED]: "success",
};

const STATUS_ICONS = {
  [WorkOrderStatus.DRAFT]: "nc-icon nc-align-left-2",
  [WorkOrderStatus.SENT]: "nc-icon nc-watch-time",
  [WorkOrderStatus.PARTIALLY_APPROVED]: "nc-icon nc-like-2",
  [WorkOrderStatus.FULLY_APPROVED]: "nc-icon nc-like-2",
  [WorkOrderStatus.DECLINED]: "nc-icon nc-simple-remove",
  [WorkOrderStatus.QUOTED]: "nc-icon nc-paper",
  [WorkOrderStatus.ACCEPTED]: "nc-icon nc-check-2",
  [WorkOrderStatus.IN_PROGRESS]: "nc-icon nc-settings-tool-66",
  [WorkOrderStatus.DONE]: "nc-icon nc-check-2",
  [WorkOrderStatus.READY_TO_DELIVER]: "nc-icon nc-delivery-fast",
  [WorkOrderStatus.DELIVERED]: "nc-icon nc-satisfied",
};

export const statusTone = (status) => STATUS_TONES[status] ?? "muted";
export const statusIcon = (status) =>
  STATUS_ICONS[status] ?? "nc-icon nc-bullet-list-67";

export const statusLabel = (status) =>
  ({
    [WorkOrderStatus.DRAFT]: strings.wo_status_draft,
    [WorkOrderStatus.SENT]: strings.wo_status_sent,
    [WorkOrderStatus.PARTIALLY_APPROVED]: strings.wo_status_partially_approved,
    [WorkOrderStatus.FULLY_APPROVED]: strings.wo_status_fully_approved,
    [WorkOrderStatus.DECLINED]: strings.wo_status_declined,
    [WorkOrderStatus.QUOTED]: strings.wo_status_quoted,
    [WorkOrderStatus.ACCEPTED]: strings.wo_status_accepted,
    [WorkOrderStatus.IN_PROGRESS]: strings.wo_status_in_progress,
    [WorkOrderStatus.DONE]: strings.wo_status_done,
    [WorkOrderStatus.READY_TO_DELIVER]: strings.wo_status_ready_to_deliver,
    [WorkOrderStatus.DELIVERED]: strings.wo_status_delivered,
  }[status] ?? status);

export const itemTypeMeta = (type) =>
  ({
    [ItemType.PRIORITY]: {
      label: strings.wo_type_priority,
      tone: "danger",
      icon: "nc-icon nc-alert-circle-i",
    },
    [ItemType.OPTIONAL]: {
      label: strings.wo_type_optional,
      tone: "warning",
      icon: "nc-icon nc-bulb-63",
    },
    [ItemType.SERVICE]: {
      label: strings.wo_type_service,
      tone: "info",
      icon: "nc-icon nc-settings-gear-65",
    },
  }[type] ?? { label: type, tone: "muted", icon: "nc-icon nc-bullet-list-67" });

export const itemStatusMeta = (status) =>
  ({
    [ItemStatus.PENDING]: { label: strings.wo_item_pending, tone: "warning" },
    [ItemStatus.APPROVED]: { label: strings.wo_item_approved, tone: "success" },
    [ItemStatus.REJECTED]: { label: strings.wo_item_rejected, tone: "danger" },
  }[status] ?? { label: status, tone: "muted" });

// ── what the stage allows ───────────────────────────────────────────────────

/** Items can be built up until the final quote locks the order. */
export const canEditItems = (status) =>
  [
    WorkOrderStatus.DRAFT,
    WorkOrderStatus.SENT,
    WorkOrderStatus.PARTIALLY_APPROVED,
    WorkOrderStatus.FULLY_APPROVED,
  ].includes(status);

/** The client has answered the review, so the quote can be priced. */
export const canQuote = (status) =>
  [
    WorkOrderStatus.PARTIALLY_APPROVED,
    WorkOrderStatus.FULLY_APPROVED,
  ].includes(status);

/** Before/after proof only exists once there is work to document. */
export const canAttachPhotos = (status) =>
  [WorkOrderStatus.IN_PROGRESS, WorkOrderStatus.DONE].includes(status);

/** A promised date can slip, so it stays editable until the order closes. */
export const canChangeDelivery = (status) =>
  ![WorkOrderStatus.DELIVERED, WorkOrderStatus.DECLINED].includes(status);

export const isTerminal = (status) =>
  [WorkOrderStatus.DELIVERED, WorkOrderStatus.DECLINED].includes(status);

// ── items ───────────────────────────────────────────────────────────────────

/** Depth-first rows, parents before their children. */
export const flattenItems = (items, depth = 0, acc = []) => {
  (items ?? []).forEach((item) => {
    acc.push({ item, depth });
    flattenItems(item.children ?? [], depth + 1, acc);
  });
  return acc;
};

const costOf = (item) => {
  const raw = item?.estimatedCost;
  if (typeof raw === "number") return raw;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Estimated total across the whole tree, children included. */
export const totalEstimated = (items) =>
  (items ?? []).reduce(
    (sum, item) => sum + costOf(item) + totalEstimated(item.children),
    0
  );

/**
 * The tasks of an accepted job: its approved top-level items. Each mechanic
 * marks their own done in the app, and the last one finishes the job.
 */
export const tasksOf = (items) =>
  (items ?? []).filter((item) => item.status === ItemStatus.APPROVED);

/** `{ done, total }` over the tasks, or null when the job has none. */
export const tasksProgress = (items) => {
  const tasks = tasksOf(items);
  if (tasks.length === 0) return null;
  return {
    done: tasks.filter((task) => Boolean(task.doneAt)).length,
    total: tasks.length,
  };
};

/**
 * The parts list is worked while the approved job waits to start and while it
 * is under way; before approval it does not exist, after it is history.
 */
export const partsOpen = (status) =>
  [WorkOrderStatus.ACCEPTED, WorkOrderStatus.IN_PROGRESS].includes(status);

/** `1.5` → "1.5", `2` → "2" — hours as the app prints them. */
export const formatHours = (hours) => {
  const number = typeof hours === "number" ? hours : parseFloat(hours);
  if (Number.isNaN(number)) return "";
  return Number.isInteger(number) ? `${number}` : `${+number.toFixed(2)}`;
};

/** A parent whose children are options the client picks exactly one of. */
export const isChoiceGroup = (item) => item?.selectionMode === "one";

/**
 * An amount in the currency it was priced in.
 *
 * The symbol comes from the work order's own frozen currency rather than from
 * the garage's current setting, so a garage that switches currency does not
 * re-label the invoices it has already issued — see
 * `WorkOrderService._freezeCurrency` on the API side.
 *
 * A one-character symbol sits tight against the figure ("$12.00"), a longer
 * one is spaced off it ("AED 12.00"). No symbol formats the bare number, which
 * is what orders opened before the currency snapshot existed fall back to.
 */
export const money = (value, symbol) => {
  const number = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(number)) return "—";
  const amount = number.toFixed(2);
  const sign = `${symbol ?? ""}`.trim();
  if (!sign) return amount;
  return sign.length === 1 ? `${sign}${amount}` : `${sign} ${amount}`;
};

/** The symbol a work order's amounts are shown in, if it carries one. */
export const currencySymbolOf = (workOrder) =>
  workOrder?.currency?.symbol ?? workOrder?.payment?.currency?.symbol ?? "";

// ── delivery ────────────────────────────────────────────────────────────────

export const DeliveryUrgency = {
  OVERDUE: "overdue",
  TODAY: "today",
  SOON: "soon",
  LATER: "later",
  NONE: "none",
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Same thresholds the app marks a promised date with. */
export const deliveryUrgencyOf = (deliveryAt, now = Date.now()) => {
  if (!deliveryAt) return DeliveryUrgency.NONE;
  const remaining = new Date(deliveryAt).getTime() - now;
  if (remaining < 0) return DeliveryUrgency.OVERDUE;
  if (remaining <= DAY_MS) return DeliveryUrgency.TODAY;
  if (remaining <= 3 * DAY_MS) return DeliveryUrgency.SOON;
  return DeliveryUrgency.LATER;
};

export const deliveryUrgencyTone = (urgency) =>
  ({
    [DeliveryUrgency.OVERDUE]: "danger",
    [DeliveryUrgency.TODAY]: "warning",
    [DeliveryUrgency.SOON]: "amber",
    [DeliveryUrgency.LATER]: "success",
  }[urgency] ?? "muted");

/** `dd/MM HH:mm` — the same short form the app uses, unambiguous in both. */
export const formatDeliveryAt = (deliveryAt) => {
  if (!deliveryAt) return "";
  const date = new Date(deliveryAt);
  if (Number.isNaN(date.getTime())) return "";
  const two = (value) => String(value).padStart(2, "0");
  return `${two(date.getDate())}/${two(date.getMonth() + 1)} ${two(
    date.getHours()
  )}:${two(date.getMinutes())}`;
};

/**
 * `<input type="datetime-local">` speaks local wall-clock time with no zone,
 * so an ISO string has to lose its offset on the way in and get it back on the
 * way out — sending the raw value would shift the promise by the offset.
 */
export const toLocalInputValue = (isoOrDate) => {
  if (!isoOrDate) return "";
  const date = new Date(isoOrDate);
  if (Number.isNaN(date.getTime())) return "";
  const two = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(
    date.getDate()
  )}T${two(date.getHours())}:${two(date.getMinutes())}`;
};

export const fromLocalInputValue = (value) =>
  value ? new Date(value).toISOString() : null;
